import React, { createContext, useContext, useState, useEffect } from 'react';
import { mockDomains } from '../data/mockDomains';
import { mockUsers } from '../data/mockUsers';
import { mockTransactions } from '../data/mockTransactions';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  // Current logged-in user (for demo purposes)
  const [currentUser] = useState({
    username: 'demoUser',
    displayName: 'Demo User',
    avatar: 'https://i.pravatar.cc/150?img=1',
    tier: 'pro',
    bio: 'Exploring DomainFlip!',
    memberSince: '2024-01-15',
    stats: {
      totalDomains: 8,
      totalSales: 3,
      totalVolume: 12400,
      winRate: 50,
      avgFlipTime: 45,
      roi: 23
    }
  });

  // All domains data (will be populated from mock data)
  const [domains, setDomains] = useState(mockDomains);

  // All users data
  const [users] = useState(mockUsers);

  // Transactions
  const [transactions, setTransactions] = useState(mockTransactions);

  // User's portfolio (domains they own)
  const [portfolio, setPortfolio] = useState([]);

  // User's watchlist
  const [watchlist, setWatchlist] = useState([]);

  // User's notifications
  const [notifications, setNotifications] = useState([]);

  // Initialize demo user portfolio with a few domains
  useEffect(() => {
    // Give demo user a few domains to start with
    const demoPortfolio = [
      {
        id: 100,
        name: 'MyStartup.com',
        price: 3500,
        aiValuation: { min: 3200, max: 4000, confidence: 85 },
        status: 'unlisted',
        owner: 'demoUser',
        purchasedAt: '2024-01-10T10:00:00Z'
      },
      {
        id: 101,
        name: 'TechVenture.io',
        price: 2800,
        aiValuation: { min: 2500, max: 3200, confidence: 82 },
        status: 'unlisted',
        owner: 'demoUser',
        purchasedAt: '2024-01-12T14:30:00Z'
      }
    ];
    setPortfolio(demoPortfolio);
  }, []);

  // Domain operations
  const buyDomain = (domainId, price) => {
    const domain = domains.find(d => d.id === domainId);
    if (!domain) return;

    // Update domain status
    setDomains(prev => prev.map(d =>
      d.id === domainId
        ? { ...d, status: 'sold', soldTo: currentUser.username }
        : d
    ));

    // Add to portfolio
    setPortfolio(prev => [...prev, {
      ...domain,
      owner: currentUser.username,
      purchasedAt: new Date().toISOString(),
      status: 'unlisted'
    }]);

    // Add transaction
    const newTransaction = {
      id: transactions.length + 1,
      type: 'purchase',
      domain: domain.name,
      domainId,
      price,
      buyer: currentUser.username,
      seller: domain.listedBy,
      timestamp: new Date().toISOString(),
    };
    setTransactions(prev => [newTransaction, ...prev]);

    // Add notification
    addNotification({
      type: 'success',
      message: `Successfully purchased ${domain.name} for $${price.toLocaleString()}`,
      timestamp: new Date().toISOString()
    });
  };

  const listDomain = (domainId, listingData) => {
    setDomains(prev => prev.map(d =>
      d.id === domainId
        ? { ...d, status: 'listed', ...listingData, listedAt: new Date().toISOString() }
        : d
    ));

    setPortfolio(prev => prev.map(d =>
      d.id === domainId
        ? { ...d, status: 'listed', ...listingData }
        : d
    ));

    addNotification({
      type: 'success',
      message: `Successfully listed domain for sale`,
      timestamp: new Date().toISOString()
    });
  };

  const placeBid = (domainId, amount) => {
    setDomains(prev => prev.map(d =>
      d.id === domainId
        ? {
            ...d,
            bids: [...(d.bids || []), {
              username: currentUser.username,
              amount,
              timestamp: new Date().toISOString(),
            }].sort((a, b) => b.amount - a.amount)
          }
        : d
    ));

    addNotification({
      type: 'success',
      message: `Bid of $${amount.toLocaleString()} placed successfully`,
      timestamp: new Date().toISOString()
    });
  };

  const toggleLike = (domainId) => {
    setDomains(prev => prev.map(d => {
      if (d.id === domainId) {
        const isLiked = d.likedBy?.includes(currentUser.username);
        return {
          ...d,
          likes: isLiked ? d.likes - 1 : d.likes + 1,
          likedBy: isLiked
            ? d.likedBy.filter(u => u !== currentUser.username)
            : [...(d.likedBy || []), currentUser.username]
        };
      }
      return d;
    }));
  };

  const addToWatchlist = (domainId) => {
    if (!watchlist.includes(domainId)) {
      setWatchlist(prev => [...prev, domainId]);
      addNotification({
        type: 'info',
        message: 'Domain added to watchlist',
        timestamp: new Date().toISOString()
      });
    }
  };

  const removeFromWatchlist = (domainId) => {
    setWatchlist(prev => prev.filter(id => id !== domainId));
  };

  const addNotification = (notification) => {
    setNotifications(prev => [
      { id: Date.now(), ...notification },
      ...prev
    ].slice(0, 50)); // Keep only last 50 notifications
  };

  const clearNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const getDomainById = (id) => {
    return domains.find(d => d.id === parseInt(id));
  };

  const getDomainByName = (name) => {
    return domains.find(d => d.name.toLowerCase() === name.toLowerCase());
  };

  const getUserByUsername = (username) => {
    if (username === currentUser.username) return currentUser;
    return users.find(u => u.username === username);
  };

  const value = {
    currentUser,
    domains,
    setDomains,
    users,
    transactions,
    portfolio,
    watchlist,
    notifications,
    buyDomain,
    listDomain,
    placeBid,
    toggleLike,
    addToWatchlist,
    removeFromWatchlist,
    addNotification,
    clearNotification,
    getDomainById,
    getDomainByName,
    getUserByUsername,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
